import React, { useState, useEffect, useCallback } from "react";
import MainLayout from "../layouts/MainLayout";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  ArrowRight,
  MapPin,
  ShieldCheck,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [resendSec, setResendSec] = useState(0);
  const [otpExpiresMin, setOtpExpiresMin] = useState(10);

  useEffect(() => {
    if (resendSec <= 0) return;
    const t = setInterval(() => setResendSec((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendSec]);

  const sendOtp = useCallback(async () => {
    return authService.sendForgotPasswordOtp({ email: email.trim() });
  }, [email]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (!email.trim()) {
      setError("Vui lòng nhập email.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await sendOtp();
      if (typeof data.expiresInMinutes === "number") {
        setOtpExpiresMin(data.expiresInMinutes);
        setStep("reset");
        setOtp("");
        setResendSec(60);
        setInfo("");
      } else {
        setInfo(data.message || "Nếu email đã đăng ký, bạn sẽ nhận được mã OTP.");
      }
    } catch (err) {
      setError(err.message || "Đã có lỗi xảy ra.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendSec > 0 || submitting) return;
    setError("");
    setSubmitting(true);
    try {
      const data = await sendOtp();
      if (typeof data.expiresInMinutes === "number") {
        setResendSec(60);
      }
    } catch (err) {
      setError(err.message || "Không gửi lại được mã.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    const code = otp.replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setError("Vui lòng nhập đủ 6 chữ số mã OTP.");
      return;
    }
    if (newPassword.length < 8) {
      setError("Mật khẩu mới tối thiểu 8 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Mật khẩu xác nhận không khớp.");
      return;
    }

    setSubmitting(true);
    try {
      const data = await authService.resetPasswordWithOtp({
        email: email.trim(),
        otp: code,
        newPassword,
      });
      if (data.token && data.user) {
        login({ token: data.token, refreshToken: data.refreshToken, user: data.user });
        navigate(data.user.role === "admin" ? "/admin/dashboard" : "/", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError(err.message || "Đặt lại mật khẩu thất bại.");
    } finally {
      setSubmitting(false);
    }
  };

  const maskEmail = (em) => {
    const [u, d] = em.split("@");
    if (!d) return em;
    return `${u.slice(0, 2)}•••@${d}`;
  };

  return (
    <MainLayout>
      <div className="bg-parchment flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden px-2 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:px-3 sm:py-2.5 md:px-4">
      <div className="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-1 flex-col">
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-brand-border/70 bg-card shadow-soft sm:rounded-2xl sm:shadow-soft-lg">
          <div className="grid h-full min-h-0 flex-1 grid-cols-1 lg:grid-cols-2 lg:grid-rows-1">
            <div className="relative hidden min-h-0 lg:block">
              <img
                src="https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=1400&q=80"
                alt="Đà Lạt"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-vintage-dark" />
              <div className="relative z-10 flex h-full min-h-0 flex-col justify-between gap-3 p-4 text-white xl:p-6">
                <div className="min-w-0">
                  <div className="mb-2 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/20 bg-white/10 backdrop-blur-sm sm:h-10 sm:w-10 sm:rounded-xl">
                    <MapPin size={18} />
                  </div>
                  <p className="mb-1 text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-white/85 sm:text-[0.65rem] sm:tracking-[0.24em]">
                    Account recovery
                  </p>
                  <h1 className="font-faustina text-2xl font-bold italic leading-tight sm:text-3xl xl:text-4xl 2xl:text-5xl">
                    Quên mật khẩu?
                  </h1>
                  <p className="mt-2 line-clamp-4 max-w-md text-xs leading-snug text-white/90 sm:mt-3 sm:text-sm sm:leading-relaxed">
                    Nhập email đã đăng ký — chúng tôi gửi mã OTP để bạn đặt mật khẩu mới an toàn.
                  </p>
                </div>
                <div className="shrink-0 rounded-lg border border-white/20 bg-white/10 p-3 backdrop-blur-md sm:rounded-xl sm:p-4">
                  <p className="text-[0.7rem] font-medium leading-snug text-white sm:text-xs sm:leading-relaxed">
                    Mã OTP có thời hạn ngắn; không chia sẻ mã với người khác.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden lg:max-h-full">
              <div className="mx-auto flex h-full min-h-0 w-full max-w-xl flex-1 flex-col gap-2 overflow-hidden px-3 py-2 sm:gap-2 sm:px-4 sm:py-2 md:px-5 md:py-2.5">
                <div className="shrink-0">
                  <p className="mb-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.18em] text-accent/80 sm:text-[0.65rem] sm:tracking-[0.2em]">
                    {step === "email" ? "Forgot password" : "Reset password"}
                  </p>
                  <h2 className="text-balance text-2xl font-bold tracking-tight text-text-main sm:text-3xl md:text-4xl">
                    {step === "email" ? "Khôi phục tài khoản" : "Đặt mật khẩu mới"}
                  </h2>
                  <p
                    className={
                      step === "email"
                        ? "mt-1 line-clamp-2 text-xs font-medium leading-snug text-gray-700 sm:text-sm [@media(max-height:620px)]:hidden"
                        : "mt-1 line-clamp-3 text-xs font-medium leading-snug text-gray-700 sm:text-sm"
                    }
                  >
                    {step === "email"
                      ? "Dùng email bạn đã dùng khi đăng ký tài khoản người dùng."
                      : `Mã đã gửi tới ${maskEmail(email.trim())}. Hiệu lực khoảng ${otpExpiresMin} phút.`}
                  </p>
                </div>

                {step === "email" ? (
                  <form
                    onSubmit={handleSendOtp}
                    className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden sm:gap-2.5 [@media(max-height:620px)]:gap-1.5"
                  >
                    <div className="min-w-0 shrink-0">
                      <label className="mb-0.5 block text-xs font-bold text-text-main">Email</label>
                      <div className="flex min-w-0 items-center gap-1.5 rounded-lg border border-brand-border bg-[#fffdf8] px-2.5 py-2 shadow-soft transition focus-within:border-nelson focus-within:ring-2 focus-within:ring-[rgba(79,111,82,0.18)] sm:gap-2 sm:rounded-xl sm:px-3">
                        <Mail size={16} className="shrink-0 text-text-soft" />
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="email@example.com"
                          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500"
                          required
                        />
                      </div>
                    </div>

                    {info ? (
                      <div className="shrink-0 rounded-lg border-2 border-emerald-400 bg-emerald-50 px-2.5 py-2 text-xs font-medium text-emerald-900 sm:rounded-xl sm:text-sm">
                        {info}
                      </div>
                    ) : null}
                    {error ? (
                      <div className="shrink-0 rounded-lg border-2 border-red-300 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-800 sm:rounded-xl sm:py-2 sm:text-sm">
                        {error}
                      </div>
                    ) : null}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="mt-auto inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-full bg-nelson px-4 py-2.5 text-sm font-bold text-white shadow-soft-lg ring-1 ring-black/10 transition hover:bg-primary-hover disabled:opacity-60 sm:text-base"
                    >
                      {submitting ? "Đang gửi..." : "Gửi mã OTP"}
                      <ArrowRight size={16} />
                    </button>
                  </form>
                ) : (
                  <form
                    onSubmit={handleReset}
                    className="grid min-h-0 flex-1 auto-rows-min grid-cols-1 content-start gap-x-2 gap-y-2 overflow-hidden sm:grid-cols-2 sm:gap-x-3 sm:gap-y-2 [@media(max-height:620px)]:gap-y-1.5"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setStep("email");
                        setOtp("");
                        setNewPassword("");
                        setConfirmPassword("");
                        setError("");
                        setInfo("");
                      }}
                      className="inline-flex shrink-0 items-center gap-1.5 text-xs font-semibold text-gray-700 transition hover:text-[#224d1a] sm:col-span-2 sm:text-sm"
                    >
                      <ArrowLeft size={14} />
                      Đổi email khác
                    </button>

                    <div className="flex shrink-0 justify-center py-0.5 sm:col-span-2">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl border-2 border-[#224d1a]/30 bg-[#224d1a]/15 text-[#1a3d14] sm:h-12 sm:w-12 sm:rounded-2xl">
                        <ShieldCheck size={22} />
                      </div>
                    </div>

                    <div className="min-w-0 shrink-0 sm:col-span-2">
                      <label className="mb-0.5 block text-center text-xs font-bold text-[#1a3d14] sm:text-sm">
                        Mã OTP (6 số)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder="••••••"
                        className="w-full min-w-0 rounded-lg border-2 border-gray-300 bg-white py-2 text-center font-mono text-lg tracking-[0.2em] text-[#1a3d14] shadow-sm outline-none transition placeholder:text-gray-400 focus:border-[#224d1a] focus:ring-2 focus:ring-[#224d1a]/25 sm:rounded-xl sm:py-2.5 sm:text-xl sm:tracking-[0.35em]"
                      />
                    </div>

                    <div className="min-w-0 sm:col-span-1">
                      <label className="mb-0.5 block text-xs font-bold text-[#1a3d14]">Mật khẩu mới</label>
                      <div className="flex min-w-0 items-center gap-1.5 rounded-lg border-2 border-gray-300 bg-white px-2.5 py-2 shadow-sm transition focus-within:border-[#224d1a] focus-within:ring-2 focus-within:ring-[#224d1a]/25 sm:gap-2 sm:rounded-xl sm:px-3">
                        <Lock size={16} className="shrink-0 text-gray-600" />
                        <input
                          type={showPw ? "text" : "password"}
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="≥ 8 ký tự"
                          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500"
                          minLength={8}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPw((v) => !v)}
                          className="shrink-0 text-gray-600 transition hover:text-[#224d1a]"
                        >
                          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    <div className="min-w-0 sm:col-span-1">
                      <label className="mb-0.5 block text-xs font-bold text-[#1a3d14]">Xác nhận MK</label>
                      <div className="flex min-w-0 items-center gap-1.5 rounded-lg border-2 border-gray-300 bg-white px-2.5 py-2 shadow-sm transition focus-within:border-[#224d1a] focus-within:ring-2 focus-within:ring-[#224d1a]/25 sm:gap-2 sm:rounded-xl sm:px-3">
                        <Lock size={16} className="shrink-0 text-gray-600" />
                        <input
                          type={showPw2 ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Nhập lại"
                          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-500"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPw2((v) => !v)}
                          className="shrink-0 text-gray-600 transition hover:text-[#224d1a]"
                        >
                          {showPw2 ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>

                    {error ? (
                      <div className="rounded-lg border-2 border-red-300 bg-red-50 px-2.5 py-1.5 text-xs font-medium text-red-800 sm:col-span-2 sm:rounded-xl sm:py-2 sm:text-sm">
                        {error}
                      </div>
                    ) : null}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex w-full shrink-0 items-center justify-center gap-1.5 rounded-full bg-[#224d1a] px-4 py-2.5 text-sm font-bold text-white shadow-md shadow-[#224d1a]/40 ring-1 ring-black/10 transition hover:bg-[#1a3d14] disabled:opacity-60 sm:col-span-2 sm:text-base"
                    >
                      {submitting ? "Đang lưu..." : "Xác nhận & đăng nhập"}
                      <ArrowRight size={16} />
                    </button>

                    <div className="shrink-0 text-center sm:col-span-2">
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendSec > 0 || submitting}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1a3d14] underline-offset-2 hover:underline disabled:text-gray-500 sm:text-sm"
                      >
                        <RefreshCw size={12} />
                        {resendSec > 0 ? `Gửi lại mã (${resendSec}s)` : "Gửi lại mã OTP"}
                      </button>
                    </div>
                  </form>
                )}

                <p className="shrink-0 pt-1 text-center text-xs font-medium text-gray-700 sm:pt-2 sm:text-sm">
                  <Link
                    to="/login"
                    className="font-bold text-[#1a3d14] underline-offset-2 hover:underline"
                  >
                    ← Quay lại đăng nhập
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </MainLayout>
  );
};

export default ForgotPassword;
